import React, {
  FC,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  Dimensions,
  Pressable,
  TouchableWithoutFeedback,
  Platform,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import styled, { css } from 'styled-components/native';
import ChatBubble from './chatBubble';
import { useTheme } from 'styled-components';
import { Theme } from '../../theme/lightTheme';
import GenericButton from '../button';
import ProgressiveImage from '../progressiveImage';
import {
  ChatComponentProps,
  ChatScreenRef,
} from '../../interfaces/components/chatComponent';
import { tr } from '../../translations';
import ChatListItemSkeleton from './chatListItemSkeleton';
import EmptyList from '../emptyList';
import Animated, {
  FadeIn,
  FadeInDown,
  FadeInUp,
  FadeOut,
  FadeOutUp,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  ZoomInEasyDown,
} from 'react-native-reanimated';
import Loader from '../loader';
import type { MessageItem } from '../../interfaces/reducers/chat';
import { getLastMessageByUser } from '../../screens/content/chat/actions';
import { copyToClipboard } from '../../utils/helpers';
import EmptyChat from './emptyChat';
import Icon from '../icon';
import ClinicalCaseCategories from '../clinicalCaseCategories';
import {
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import { isWeb } from '../../utils/responsive';
import ReferencesCarousel from '../referencesCarousel';
import CodeDisplayBlock from '../codeDisplayBlock';
import CustomTypeWriter from '../customTypeWriter';
import { renderArticleContent } from '../../utils/html';
import { exportChatToPdf } from '../../utils/pdf';
import { showGlobalToast } from '../../connected-components/toast/actions';
import { ToastType } from '../../utils/enums';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { useNavigation } from '@react-navigation/native';
import screens from '../../config/screens';
import { sendMessage, resetChat, getChatHistory, submitQNAStreaming } from '../../screens/content/chat/actions';
import { setMessages, setCurrentThreadId } from '../../screens/content/chat/reducer';
import { ChatCategory } from '../../interfaces/api/auth/login';
import { useCustomHeaderHeight } from '../../utils/hooks/useHeaderHeight';
import { useDifferentialDiagnosisTable } from '../../hooks/useDifferentialDiagnosisTable';
import { TreatmentOptionsFAB, UCAFFAB, DrugsTreatmentFAB } from '../differentialDiagnosisTable';
import { DIFFERENTIAL_DIAGNOSIS_CATEGORY_IDS, staticChatTabs } from '../../assets/static/chat';
import {
  CategoryKeys,
  CategoryIDs,
  isDifferentialDiagnosisCategory,
  isLatestTreatmentOptionsCategory,
  isDrugsTreatmentCategory,
  isEnhancedTableCategory,
  getCategoryKeyByIdFallback,
} from '../../utils/categoryKeys';

// Helper function to map category ID to category_key
const getCategoryKeyById = (categoryId: number): string | null => {
  switch (categoryId) {
    case 1:
    case 69:  // Nurse
    case 53:  // Surgeon
    case 316: // Other differential diagnosis IDs
      return CategoryKeys.DIFFERENTIAL_DIAGNOSIS;
    case 4:
      return CategoryKeys.LATEST_TREATMENT_OPTIONS;
    case 6:
      return CategoryKeys.DRUGS_TREATMENT;
    default:
      return null;
  }
};

// Enhanced styled components for web-specific design
const Container = styled(Animated.View)`
  flex: 1;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const MessagesContainer = styled.ScrollView<{
  isRTL?: boolean;
  isEmpty?: boolean;
  fullHeight?: boolean;
}>`
  width: 100%;
  flex: 1;
  padding-bottom: ${({ theme }) => theme.text.s1}px;
  height: ${({ fullHeight }) => (fullHeight ? '100vh' : '78vh')};
  pointer-events: ${({ isEmpty }) => (isEmpty ? 'none' : 'auto')};
  padding-top: ${({ fullHeight }) => (fullHeight ? '100px' : '20px')};
`;

const WebMessagesContainer = styled(ScrollView).withConfig({
  shouldForwardProp: (prop) => !['headerHeight', 'isEmpty'].includes(prop),
}) <{
  headerHeight: number;
  isEmpty?: boolean;
}>`
  flex: 1;
  padding: 20px;
  margin-top: ${({ headerHeight }: { headerHeight: number }) => headerHeight}px;
  pointer-events: ${({ isEmpty }: { isEmpty?: boolean }) => (isEmpty ? 'none' : 'auto')};
  min-height: 200px;
`;

const WebMessagesList = styled.View`
  flex: 1;
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
  justify-content: flex-start;
`;

const MessageItem = styled(Animated.View) <{ isUser: boolean; isRTL?: boolean }>`
  display: flex;
  position: relative;
  flex-direction: column;
  overflow: visible;
  gap: 1rem;
  padding: 1rem;
  align-items: ${({ isUser, isRTL }) => {
    if (isRTL) {
      return isUser ? 'flex-start' : 'flex-end';
    }
    return isUser ? 'flex-end' : 'flex-start';
  }};
`;

const MessageBubble = styled.View<{ isUser?: boolean; isRTL?: boolean }>`
  padding: 1rem;
  max-width: ${({ theme }) => (theme.isMobile || false ? '73vw' : '80%')};
  min-width: 8vw;
  background: ${({ theme }) => theme.cardBackground};
  flex-direction: column;
  border-radius: ${({ isUser, isRTL }) => {
    if (isRTL) {
      return isUser ? '20px 20px 20px 3px' : '20px 20px 3px 20px';
    }
    return isUser ? '20px 20px 3px 20px' : '20px 20px 20px 3px';
  }};
  font-size: ${({ theme }) => theme.text.s5}px;
  text-align: ${({ isRTL }) => (isRTL ? 'right' : 'left')};
  direction: ${({ isRTL }) => (isRTL ? 'rtl' : 'ltr')};
`;

const AvatarContainer = styled(Animated.View)`
  width: ${({ theme }) => theme.text.s3}px;
  aspect-ratio: 1;
  border-radius: 100px;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  margin-bottom: -0.5%;
  border: 1px solid ${({ theme }) => theme.primary};
  display: flex;
  background: ${({ theme }) =>
    theme.voiceQuestionInput?.bubbleBackground || theme.cardBackground};
  box-shadow: 0 4px 8px ${({ theme }) => theme.shadow || 'rgba(0,0,0,0.1)'};
  border: ${({ theme }) => theme.text.grey + '22'} solid 1px;
`;

const AssistantMessageContainer = styled(Animated.View) <{ isRTL?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0 0;
  width: ${({ theme }) => (theme.isMobile ? '74vw' : 'calc(100vw - 30rem)')};
  color: ${({ theme }) => theme.text.dark};
  text-align: ${({ isRTL }) => (isRTL ? 'right' : 'left')};
`;

const OptionIconContainer = styled.TouchableOpacity`
  width: ${({ theme }: { theme: any }) => theme.text.s1 * 1.3}px;
  height: ${({ theme }: { theme: any }) => theme.text.s1 * 1.3}px;
  background-color: ${({ theme }: { theme: any }) => theme.primary};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  shadow-color: ${({ theme }: { theme: any }) => theme.text.dark};
  shadow-offset: 2px 4px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 1;
  gap: 6px;
  color: ${({ theme }: { theme: any }) => theme.text.grey};
  font-size: 0.875rem;
  background-color: transparent;
  border: none;
  min-width: 80px;
  font-weight: 500;
`;

const AssistantOptionContainer = styled(Animated.View) <{
  isUser: boolean;
  isRTL?: boolean;
}>`
  display: flex;
  gap: ${({ theme }) => theme.text.s11}px;
  align-items: center;
  flex-direction: row;
  bottom: 5px;
  z-index: 2;
  justify-content: ${({ isUser, isRTL }) => {
    if (isRTL) {
      return isUser ? 'flex-end' : 'flex-start';
    }
    return isUser ? 'flex-end' : 'flex-start';
  }};
`;

const AnimatedIntroText = styled(Animated.Text)`
  font-size: ${({ theme }) => theme.text.s3}px;
  color: ${({ theme }) => theme.text.light};
  margin-bottom: 1rem;
  text-align: center;
`;

const QuickQuestionsContainer = styled.View<{ isRTL?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  width: 100%;
`;

const QuickQuestion = styled(TouchableOpacity)`
  background: ${({ theme }) =>
    theme.voiceQuestionInput?.bubbleBackground || theme.cardBackground};
  color: ${({ theme }) => theme.text.light};
  border: none;
  padding: 0.8rem 1.2rem;
  border-radius: 20px;
  font-size: ${({ theme }) => theme.text.s9}px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CategoryBullet = styled(TouchableOpacity) <{ isSelected?: boolean }>`
  background: ${({ theme, isSelected }) =>
    isSelected
      ? theme.categoryBullet?.selectedBackground || theme.primary + '15'
      : theme.categoryBullet?.background || '#f5f5f5'};
  color: ${({ theme, isSelected }) =>
    isSelected
      ? theme.categoryBullet?.selectedText || theme.primary
      : theme.categoryBullet?.text || '#333333'};
  border: ${({ theme, isSelected }) =>
    isSelected
      ? `1px solid ${theme.categoryBullet?.selectedBorder || theme.primary}`
      : 'none'};
  padding: 0.8rem 1.2rem;
  border-radius: 20px;
  font-size: ${({ theme }) => theme.text.s9}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ isSelected }) => (isSelected ? '600' : '400')};
  min-height: 40px;
  min-width: 100px;
`;

const CategoryLink = styled(TouchableOpacity) <{ isRTL?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0.5rem 0 0.35rem 0;
  margin-top: 0.5rem;
  margin-bottom: 0;
  border-radius: 0;
  background: transparent;
  color: ${({ theme }) => theme.voiceQuestionInput?.text || theme.text.dark};
  font-size: ${({ theme }) => theme.text.s9}px;
  border: none;
  border-top: 1px solid
  ${({ theme }) =>
    theme.voiceQuestionInput?.text + '33' || theme.text.dark + '33'};
  width: 100%;
  gap: 0.35rem;
  font-weight: 500;
`;

const ImageShowContainer = styled.View`
  position: absolute;
  width: 100vw;
  height: 100vh;
  background-color: ${({ theme }) => theme.cardBackground + '77'};
  z-index: 1000;
`;

const ShowImage = styled(ProgressiveImage)`
  width: 100%;
  height: 70%;
  justify-content: center;
  align-items: center;
`;

const MessageSettingsContainer = styled(Animated.View)`
  position: absolute;
  width: 100%;
  height: 100vh;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const MessageSettingsContentContainer = styled(Pressable)`
  position: absolute;
  width: 100%;
  height: 100vh;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.text.dark + '44'};
`;

const SettingsContainer = styled.View`
  width: 40%;
  background-color: ${({ theme }) => theme.homeBackground};
  border-radius: 8px;
  overflow: hidden;
`;

const EmptyListContainer = styled(Animated.View) <{
  responsiveHeight: number;
  responsiveWidth: number;
}>`
  align-self: center;
  position: absolute;
  top: ${({ responsiveHeight }) => responsiveHeight * 0.25}px;
  width: ${({ responsiveWidth }) => responsiveWidth * 0.8}px;
  min-height: ${({ responsiveHeight }) => responsiveHeight * 0.23}px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.homeBackground + '77'};
  z-index: 99;
`;

const FooterContainer = styled(Animated.View)`
  padding-bottom: 5%;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

const LoadingContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const ClinicalCaseContainer = styled.View<{
  headerHeight: number;
  isWeb: boolean;
  paddingHorizontal: number;
}>`
  flex: 1;
  padding-top: ${({ headerHeight }) => headerHeight}px;
  padding-horizontal: ${({ isWeb, paddingHorizontal }) =>
    isWeb ? 0 : paddingHorizontal}px;
  height: 100%;
  overflow: hidden;
`;

const ClinicalCaseScrollView = styled.ScrollView`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
`;

const FabContainer = styled(Animated.View)`
  position: absolute;
  left: 0;
  bottom: ${({ theme }) => theme.text.s8}px;
  z-index: 10;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const FabButton = styled.TouchableOpacity`
  background-color: ${({ theme }) => theme.screenBackground};
  padding: ${({ theme }) => theme.text.s4 / 2}px;
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  shadow-color: #000;
  shadow-offset: 0px 2px;
  shadow-opacity: 0.3;
  shadow-radius: 4px;
  transition: all 0.2s ease-in-out;

  &:active {
    transform: scale(0.95);
    shadow-opacity: 0.5;
  }
`;

/**
 * ChatComponentWeb renders a web-optimized chat interface with message bubbles, settings, and image previews.
 * Uses div-based scrolling instead of FlatList for better web performance.
 * @param {ChatComponentProps} props - Component props.
 * @returns {React.ReactElement} The ChatComponentWeb.
 */
const ChatComponentWeb: FC<ChatComponentProps> = forwardRef<
  ChatScreenRef,
  ChatComponentProps
>(
  (
    {
      data,
      userId,
      userName,
      // onDeleteMessage,
      isWriting,
      isLoading,
      isError,
      isMessageSendingError,
      isMessageSendingLoading,
      onRefresh,
      onMessageTryAgain,
      onEndReached,
      isLoadingMore,
      streamMessageLoadingId,
      onStreamingMessageEndAnimation,
      // New props
      references,
      metadata,
      layoutDirection = 'ltr',
      onSharePress,
      onAddToFavorite,
      isFavorite = false,
      shared = false,
      onReadTextPress,
      isSpeaking = false,
      onDebugButtonPress,
      showDebug = false,
      categories,
      onCategoryPress,
      selectedCategory,
      onQuickQuestionPress,
      onTryWithOtherCategory,
      fullHeight = false,
      categoryId,
      categoryKey: propsCategoryKey,
      ...props
    },
    ref,
  ): React.ReactElement => {
    const theme = useTheme() as Theme;
    const { wdp, hdp } = useResponsiveDimensions();
    // Custom header height.
    const headerHeight = useCustomHeaderHeight();
    const [selectedItem, setSelectedItem] = useState<any>();
    const [messageState, setMessageState] = useState<boolean>(false);
    const [showMessageState, setShowMessageState] = useState<boolean>(false);
    const [imageShowState, setImageShowState] = useState<boolean>(false);
    const [showItemImage, setShowItemImage] = useState<string>();
    const lastUserMessage = getLastMessageByUser(data);
    const opacity = useSharedValue(0);
    const messagesContainerRef = useRef<ScrollView>(null);
    const { selectedCase } = useSelector((state: RootState) => state.chat);

    // FAB animation shared values
    const isAtBottomButtonScroll = useSharedValue(true);
    const fabPressScale = useSharedValue(1);

    // Auto-scroll state management
    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const autoScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastScrollY = useRef(0);
    const scrollThreshold = 10; // 10px threshold for stopping auto-scroll

    // Redux state for user info
    const { currentUser } = useSelector((state: RootState) => state.auth);
    const navigation = useNavigation();
    const dispatch = useDispatch();

    // The chat creation is handled automatically by submitQNAStreaming
    // when the first chunk with chat_id arrives from the backend

    // Differential Diagnosis Feature
    const {
      selectedRows,
      toggleRowSelection,
      clearSelection,
      getSelectedRowsReadable,
    } = useDifferentialDiagnosisTable();

    const { isDifferentialDiagnosis, isLatestTreatmentOptions, isDrugsTreatment, shouldShowUCAFFAB, shouldShowDiagnosisCheckboxes, shouldShowTreatmentCheckboxes, categoryKey, categoryId: detectedCategoryId } = useMemo(() => {
      // Check category during streaming if categoryId or propsCategoryKey is available (from navigation params)
      const isStreamingCompleted = !isWriting && !streamMessageLoadingId;

      // CRITICAL FIX: If streaming but we have categoryId or propsCategoryKey from navigation (FAB navigation),
      // use it to determine category immediately so checkboxes show during streaming
      if (!isStreamingCompleted && (categoryId || propsCategoryKey)) {
        // Prioritize deriving categoryKey from categoryId (e.g., 4 for treatment options) for API consistency
        const effectiveCategoryKey = categoryId
          ? getCategoryKeyByIdFallback(categoryId) || propsCategoryKey
          : propsCategoryKey;

        const isDifferentialDiagnosis = isDifferentialDiagnosisCategory(effectiveCategoryKey || undefined);
        const isLatestTreatmentOptions = isLatestTreatmentOptionsCategory(effectiveCategoryKey || undefined);
        const isDrugsTreatment = isDrugsTreatmentCategory(effectiveCategoryKey || undefined);

        return {
          isDifferentialDiagnosis,
          isLatestTreatmentOptions,
          isDrugsTreatment,
          shouldShowUCAFFAB: isDrugsTreatment,
          shouldShowDiagnosisCheckboxes: isDifferentialDiagnosis,
          shouldShowTreatmentCheckboxes: isLatestTreatmentOptions,
          categoryKey: effectiveCategoryKey,
          categoryId: categoryId || null
        };
      }

      // If streaming and no propsCategoryKey, return false for all flags
      if (!isStreamingCompleted) {
        return {
          isDifferentialDiagnosis: false,
          isLatestTreatmentOptions: false,
          isDrugsTreatment: false,
          shouldShowUCAFFAB: false,
          shouldShowDiagnosisCheckboxes: false,
          shouldShowTreatmentCheckboxes: false,
          categoryKey: null,
          categoryId: null
        };
      }

      const assistantMessages = data.filter(msg => msg.role === 'assistant');
      const lastAssistantMessage =
        assistantMessages.length > 0 ? assistantMessages[0] : null;

      // Use category_key from the last assistant message (backend provides this)
      const messageCategoryKey = lastAssistantMessage?.category_key;
      const messageCategoryId = lastAssistantMessage?.category_id;


      // When no assistant message yet (e.g. URL with ?categoryId=4&categoryKey=latest_treatment_options), use props
      if (!lastAssistantMessage) {
        const fallbackCategoryKey = getCategoryKeyByIdFallback(categoryId);
        const effectiveCategoryKey = propsCategoryKey || fallbackCategoryKey;
        const isDifferentialDiagnosis = isDifferentialDiagnosisCategory(effectiveCategoryKey || undefined);
        const isLatestTreatmentOptions = isLatestTreatmentOptionsCategory(effectiveCategoryKey || undefined);
        const isDrugsTreatment = isDrugsTreatmentCategory(effectiveCategoryKey || undefined);
        return {
          isDifferentialDiagnosis,
          isLatestTreatmentOptions,
          isDrugsTreatment,
          shouldShowUCAFFAB: isDrugsTreatment,
          shouldShowDiagnosisCheckboxes: isDifferentialDiagnosis,
          shouldShowTreatmentCheckboxes: isLatestTreatmentOptions,
          categoryKey: effectiveCategoryKey,
          categoryId: categoryId ?? null
        };
      }

      // Implement comprehensive fallback chain
      const fallbackCategoryKey = getCategoryKeyByIdFallback(messageCategoryId ?? categoryId);
      const effectiveCategoryKey = messageCategoryKey || propsCategoryKey || fallbackCategoryKey;

      const isDifferentialDiagnosis = isDifferentialDiagnosisCategory(effectiveCategoryKey || undefined);
      const isLatestTreatmentOptions = isLatestTreatmentOptionsCategory(effectiveCategoryKey || undefined);
      const isDrugsTreatment = isDrugsTreatmentCategory(effectiveCategoryKey || undefined);

      // Checkboxes visibility is more specific:
      // - Diagnosis table checkboxes ONLY for differential_diagnosis
      // - Treatment table checkboxes ONLY for latest_treatment_options
      const shouldShowDiagnosisCheckboxes = isDifferentialDiagnosis;
      const shouldShowTreatmentCheckboxes = isLatestTreatmentOptions;

      // UCAF FAB shows for drugs treatment
      const shouldShowUCAFFAB = isDrugsTreatment;

      // Placeholder for differentialDiagnosisContext, assuming these variables are defined elsewhere or will be.
      // For a syntactically correct file, `selectedDiagnoses`, `onDiagnosisToggle`, `selectedTreatments`,
      // `onTreatmentToggle`, `onSendSelectedDiagnoses`, `onSendSelectedTreatments`, `onAddToUCAF`, `item`
      // would need to be defined in this scope.
      // As per instruction, adding the code as provided.
      const differentialDiagnosisContext = {
        isDifferentialDiagnosis,
        isLatestTreatmentOptions,
        isDrugsTreatment,
        shouldShowDiagnosisCheckboxes,
        shouldShowTreatmentCheckboxes,
        // selectedDiagnoses, // Assuming these are defined in the actual context
        // onDiagnosisToggle, // Assuming these are defined in the actual context
        // selectedTreatments, // Assuming these are defined in the actual context
        // onTreatmentToggle, // Assuming these are defined in the actual context
        // onSendSelectedDiagnoses, // Assuming these are defined in the actual context
        // onSendSelectedTreatments, // Assuming these are defined in the actual context
        // onAddToUCAF, // Assuming these are defined in the actual context
        shouldShowUCAFFAB,
      };

      return {
        isDifferentialDiagnosis,
        isLatestTreatmentOptions,
        isDrugsTreatment,
        shouldShowUCAFFAB,
        shouldShowDiagnosisCheckboxes,
        shouldShowTreatmentCheckboxes,
        categoryKey: messageCategoryKey,
        categoryId: messageCategoryId
      };
    }, [data, isWriting, streamMessageLoadingId, categoryId, propsCategoryKey]);

    // FAB visibility logic - all based on category_key after streaming ends
    const showTreatmentOptionsFAB = isDifferentialDiagnosis && selectedRows.size > 0;
    const showLatestTreatmentFAB = isLatestTreatmentOptions && selectedRows.size > 0;
    const shouldShowUCAFFABFinal = shouldShowUCAFFAB;


    // Handle Treatment Options FAB press
    const handleTreatmentOptionsFAB = useCallback(async () => {
      const selectedData = getSelectedRowsReadable();

      // Get the original user question from the chat data
      const userMessages = data.filter(msg => msg.role === 'user' || msg.sender_id === currentUser?.id);
      const originalQuestion = userMessages.length > 0 ? userMessages[0].message : '';

      // Format the selected diagnoses
      const diagnosesText = selectedData.length > 0
        ? selectedData.map(d => `- ${d}`).join('\n')
        : 'No diagnoses selected';

      // Create the enhanced question with selected diagnoses
      const enhancedQuestion = `${originalQuestion}\n\nSelected diagnoses for further analysis:\n${diagnosesText}`;

      // Find Treatment Options category ID from static data (id: 3, key: treatment_options)
      const treatmentOptionsCategory = staticChatTabs
        .flatMap(tab => tab.categories)
        .find(cat => cat.title === 'Treatment Options');
      const treatmentOptionsCategoryId = treatmentOptionsCategory?.id || 3;

      // Pass category info for the next conversation
      const categoryInfo = {
        categoryKey: 'treatment_options',
        categoryId: detectedCategoryId,
        selectedData
      };


      try {
        clearSelection();

        // Navigate first so route params (categoryId, categoryKey) are set before we reset and stream.
        // Otherwise the first render still sees old params and "where we are" is wrong until second FAB press.
        (navigation as any)?.navigate(screens.content.genioChat, {
          isQNA: false,
          tab_id: 13,
          categoryId: treatmentOptionsCategoryId,
          categoryKey: CategoryKeys.TREATMENT_OPTIONS,
          drugQuestion: enhancedQuestion,
        });

        const requestPayload = {
          question: enhancedQuestion,
          specialty_id: currentUser?.specialty_id || -1,
          question_id: treatmentOptionsCategoryId,
          user_id: currentUser?.id || -1,
          thread_id: undefined,
          tab_id: 13,
          category_key: CategoryKeys.TREATMENT_OPTIONS,
        };

        // Defer reset + stream so navigation commits and Chat screen gets new params first
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        resetChat();
        await submitQNAStreaming(requestPayload);
      } catch (error) {
      }
    }, [getSelectedRowsReadable, clearSelection, data, currentUser, navigation]);

    // Handle Drugs Treatment FAB press (for latest treatment options)
    const handleDrugsTreatmentFAB = useCallback(async () => {
      const selectedData = getSelectedRowsReadable();

      // Get the original user question from the chat data
      const userMessages = data.filter(msg => msg.role === 'user' || msg.sender_id === currentUser?.id);
      const originalQuestion = userMessages.length > 0 ? userMessages[0].message : '';

      // Format the selected drugs/classes with a meaningful prompt
      const drugsClassesText = selectedData.length > 0
        ? selectedData.map((item, index) => `${index + 1}. ${item}`).join('\n')
        : 'No drugs or classes selected';

      // Create enhanced question with meaningful context for AI
      const enhancedQuestion = `${originalQuestion}

The following drugs/classes have been selected from the treatment options for further analysis:

${drugsClassesText}

Please provide detailed information about these selected drugs/classes including their mechanisms of action, dosing guidelines, contraindications, side effects, drug interactions, and clinical considerations for the patient's condition.`;

      // Find Drugs Treatment category ID from static data
      const drugsTreatmentCategory = staticChatTabs
        .flatMap(tab => tab.categories)
        .find(cat => cat.title === 'Drugs Treatment');
      const drugsTreatmentCategoryId = drugsTreatmentCategory?.id || 6;

      // Pass category info for the next conversation
      const categoryInfo = {
        categoryKey: 'drugs_treatment',
        categoryId: detectedCategoryId,
        selectedData
      };


      try {
        clearSelection();

        // Navigate first so route params (categoryId, categoryKey) are set before we reset and stream
        (navigation as any)?.navigate(screens.content.genioChat, {
          isQNA: false,
          tab_id: 13,
          categoryId: drugsTreatmentCategoryId,
          categoryKey: CategoryKeys.DRUGS_TREATMENT,
          drugQuestion: enhancedQuestion,
        });

        const requestPayload = {
          question: enhancedQuestion,
          specialty_id: currentUser?.specialty_id || -1,
          question_id: drugsTreatmentCategoryId,
          user_id: currentUser?.id || -1,
          thread_id: undefined,
          tab_id: 13,
          category_key: CategoryKeys.DRUGS_TREATMENT,
        };

        // Defer reset + stream so navigation commits and Chat screen gets new params first
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        resetChat();
        await submitQNAStreaming(requestPayload);
      } catch (error) {
      }
    }, [getSelectedRowsReadable, clearSelection, data, currentUser, navigation]);

    // Handle Generate UCAF FAB press – open UCAF PDF in a new tab
    const handleUCAFFAB = useCallback(() => {
      const ucafPdfUrl = 'https://demo.geniomdai.com/public/NC01-014470.pdf';
      try {
        window.open(ucafPdfUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
        // fallback: allow popup blockers to show
        window.open(ucafPdfUrl, '_blank');
      }
    }, []);

    // Animated Style for the Button (Fade In/Out with smooth easing)
    const scrollButtonStyle = useAnimatedStyle(() => ({
      opacity: isAtBottomButtonScroll.value ? withTiming(0, { duration: 300 }) : withTiming(1, { duration: 300 }),
      transform: [
        { scale: isAtBottomButtonScroll.value ? withTiming(0.7, { duration: 300 }) : withTiming(1, { duration: 300 }) },
        { translateY: isAtBottomButtonScroll.value ? withTiming(20, { duration: 300 }) : withTiming(0, { duration: 300 }) },
      ],
    }));

    // FAB button press animation style with smooth easing
    const fabButtonStyle = useAnimatedStyle(() => ({
      transform: [
        { scale: fabPressScale.value },
      ],
    }));
    // Memoize data to prevent unnecessary re-renders - keep original order for web
    const memoizedData = useMemo(() => {
      return [...(data || []).slice().reverse()];
    }, [data]);

    // Check if user is at bottom of scroll
    const checkIfAtBottom = useCallback((event: any) => {
      const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
      const paddingToBottom = 20;
      const isAtBottomNow = contentOffset.y >= (contentSize.height - layoutMeasurement.height - paddingToBottom);
      setIsAtBottom(isAtBottomNow);

      // Update FAB animation based on scroll position
      isAtBottomButtonScroll.value = isAtBottomNow;

      return isAtBottomNow;
    }, []);

    // Handle scroll events with user interaction detection
    const handleScroll = useCallback((event: any) => {
      const { contentOffset } = event.nativeEvent;
      const currentScrollY = contentOffset.y;

      // Check if user scrolled up by more than threshold
      const scrolledUp = lastScrollY.current - currentScrollY > scrollThreshold;

      if (scrolledUp && shouldAutoScroll) {
        setShouldAutoScroll(false);
        setIsUserScrolling(true);
      }

      // Check if user is back at bottom
      const atBottom = checkIfAtBottom(event);
      if (atBottom && !shouldAutoScroll) {
        setShouldAutoScroll(true);
        setIsUserScrolling(false);
      }

      lastScrollY.current = currentScrollY;

      // Handle pagination (load more messages when scrolling to top)
      const isAtTop = contentOffset.y <= 0;
      if (isAtTop && onEndReached && !isLoadingMore) {
        onEndReached();
      }
    }, [onEndReached, isLoadingMore, shouldAutoScroll, checkIfAtBottom, scrollThreshold]);

    // Auto-scroll to bottom when streaming starts or new messages arrive
    const scrollToBottom = useCallback((animated: boolean = true) => {
      if (messagesContainerRef.current && shouldAutoScroll) {
        messagesContainerRef.current.scrollToEnd({ animated });
      }
    }, [shouldAutoScroll]);

    // Handle FAB button press to scroll to bottom with smooth easing
    const handleFabPress = useCallback(() => {
      if (messagesContainerRef.current) {

        // Animate button press with smooth easing
        fabPressScale.value = withTiming(0.9, { duration: 100 }, () => {
          fabPressScale.value = withTiming(1, { duration: 100 });
        });

        // For web, use smooth scrolling behavior with easing
        if (isWeb) {
          // Get the scroll container element
          const scrollElement = messagesContainerRef.current as any;
          if (scrollElement._component || scrollElement._nativeTag) {
            const element = scrollElement._component || scrollElement._nativeTag;
            if (element && element.scrollTo) {
              element.scrollTo({
                top: element.scrollHeight,
                behavior: 'smooth'
              });
            }
          }
        } else {
          // For native, use the standard scrollToEnd with animation
          messagesContainerRef.current.scrollToEnd({ animated: true });
        }

        // Re-enable auto-scroll when user manually scrolls to bottom
        setShouldAutoScroll(true);
        setIsUserScrolling(false);
        setIsAtBottom(true);
        isAtBottomButtonScroll.value = true;
      }
    }, []);

    // Effect to handle auto-scroll when new messages arrive or streaming starts
    useEffect(() => {
      if (data.length > 0) {
        // If streaming just started (isWriting became true), enable auto-scroll
        if (isWriting && !shouldAutoScroll && isAtBottom) {
          setShouldAutoScroll(true);
          setIsUserScrolling(false);
        }

        // Auto-scroll if conditions are met
        if (shouldAutoScroll && !isUserScrolling) {
          // Clear any existing timeout
          if (autoScrollTimeoutRef.current) {
            clearTimeout(autoScrollTimeoutRef.current);
          }

          // Delay scroll slightly to ensure content is rendered
          autoScrollTimeoutRef.current = setTimeout(() => {
            scrollToBottom(false);
          }, 100);
        }
      }
    }, [data.length, isWriting, shouldAutoScroll, isUserScrolling, isAtBottom, scrollToBottom]);

    // Effect to handle streaming state changes
    useEffect(() => {
      if (streamMessageLoadingId) {
        // When streaming starts, ensure auto-scroll is enabled if user is at bottom
        if (isAtBottom) {
          setShouldAutoScroll(true);
          setIsUserScrolling(false);
        }
      }
    }, [streamMessageLoadingId, isAtBottom]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (autoScrollTimeoutRef.current) {
          clearTimeout(autoScrollTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      let timeout: string | number | NodeJS.Timeout | null | undefined = null;
      if (messageState) {
        opacity.value = withTiming(1, { duration: 300 });
        setShowMessageState(true);
      } else {
        opacity.value = withTiming(0, { duration: 300 });
        timeout = setTimeout(() => setShowMessageState(false), 300);
      }
      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }, [messageState]);

    // Function to render tick icons based on message status
    const buttons = [
      selectedItem?.message && {
        text: tr('chatComponent.copy'),
        onPress: () => {
          copyToClipboard(selectedItem?.message || '');
          setMessageState(false);
        },
        iconName: 'link',
      },
      // selectedItem?.sender_id === userId||item.role === 'user' && {
      //   text: tr('chatComponent.delete'),
      //   onPress: () => {
      //     onDeleteMessage && onDeleteMessage(selectedItem);
      //     setMessageState(false);
      //   },
      //   iconName: 'delete',
      //   iconColor: theme.text.error,
      // },
    ].filter(Boolean);

    // Function to show enlarged image
    const imageShow = () => {
      if (!imageShowState) {
        return null;
      }

      return (
        <TouchableWithoutFeedback onPress={() => setImageShowState(false)}>
          <ImageShowContainer>
            <ShowImage resizeMode="contain" source={{ uri: showItemImage }} />
          </ImageShowContainer>
        </TouchableWithoutFeedback>
      );
    };

    // Function to render message settings
    const messageSettings = () => {
      if (!showMessageState) {
        return null;
      }
      return (
        <MessageSettingsContainer style={{ opacity }}>
          <MessageSettingsContentContainer
            onPress={() => setMessageState(false)}>
            {_renderChatItem({ item: selectedItem, hideArrow: true })}
            <SettingsContainer>
              {buttons.map((button, index) => (
                <GenericButton
                  key={index}
                  outline
                  label={button.text}
                  labelStyle={{
                    color: theme.text.dark,
                  }}
                  onPress={button.onPress}
                  style={{
                    borderColor:
                      index === 0 ? 'transparent' : theme.text.grey + '44',
                    borderRadius: 0,
                  }}
                  showRightIcon
                  rightIconType={'AntDesign'}
                  rightIconName={button.iconName}
                  rightIconColor={button.iconColor || theme.text.grey}
                  rightIconContainerStyle={{
                    position: 'absolute',
                    right: '7%',
                  }}
                />
              ))}
            </SettingsContainer>
          </MessageSettingsContentContainer>
        </MessageSettingsContainer>
      );
    };

    const _renderHeader = () => {
      // No longer render loading state here - moved to footer
      return null;
    };

    const _renderFooter = () => {
      if (isLoadingMore && data.length > 0) {
        return (
          <LoadingContainer>
            <Loader />
          </LoadingContainer>
        );
      }
      // Old "Reasoning..." indicator removed - now using 3-phase StreamingLoadingIndicator
      // inside each streaming message's ChatBubble (based on streamMessageLoadingId)
      return null;
    };

    const _renderChatItem = ({
      item,
      hideArrow,
    }: {
      item: MessageItem;
      hideArrow?: boolean;
    }) => {
      // Find the previous message for context (to export user question + AI answer together)
      const currentIndex = data.findIndex(msg => msg.id === item.id);
      const previousMessage =
        currentIndex >= 0 && currentIndex < data.length - 1
          ? data[currentIndex + 1]
          : undefined;

      return (
        <ChatBubble
          hideArrow={hideArrow}
          onLongPress={() => {
            setSelectedItem(item);
            setMessageState(!messageState);
          }}
          name={item?.sender_name}
          style={{
            marginBottom: theme.text.s4,
          }}
          isStreamMessageLoading={streamMessageLoadingId === item?.id}
          isLastMessage={!isWriting && data?.[0]?.id === item?.id}
          date={item?.created_at}
          onPress={() => setMessageState(false)}
          type={
            userId === item.sender_id || item.role === 'user'
              ? 'send'
              : 'received'
          }
          onImagePress={selectedImage => {
            setShowItemImage(selectedImage?.image_url);
            setImageShowState(!imageShowState);
            setMessageState(false);
          }}
          isError={isMessageSendingError && lastUserMessage?.id === item?.id}
          lastItemStatus={item?.status}
          isMessageSendingLoading={
            isMessageSendingLoading && lastUserMessage?.id === item?.id
          }
          onStreamingMessageEndAnimation={onStreamingMessageEndAnimation}
          onRefreshPress={() => onMessageTryAgain && onMessageTryAgain(item)}
          previousMessage={previousMessage}
          // Pass additional props for enhanced functionality
          references={item?.references || references}
          metadata={metadata}
          layoutDirection={layoutDirection}
          onSharePress={onSharePress}
          onAddToFavorite={onAddToFavorite}
          isFavorite={isFavorite}
          shared={shared}
          onReadTextPress={onReadTextPress}
          isSpeaking={isSpeaking}
          onDebugButtonPress={onDebugButtonPress}
          showDebug={showDebug}
          // Context with very specific checkbox visibility control
          differentialDiagnosisContext={(() => {
            const fallbackCategoryKey = getCategoryKeyByIdFallback(categoryId);
            const effectiveCategoryKey = categoryKey || propsCategoryKey || fallbackCategoryKey;


            return {
              // CRITICAL: Only show diagnosis table checkboxes when category_key === 'differential_diagnosis'
              isDifferentialDiagnosis: isDifferentialDiagnosisCategory(effectiveCategoryKey || undefined),
              // CRITICAL: Only show treatment table checkboxes when category_key === 'latest_treatment_options' or 'treatment_options'
              // DO NOT show checkboxes for drugs_treatment - that's for reading only, not selection
              isLatestTreatmentOptions: isLatestTreatmentOptionsCategory(effectiveCategoryKey || undefined),
              selectedRows,
              toggleRowSelection,
            };
          })()}
          {...item}
        />
      );
    };
    const _renderItem = (item: MessageItem, index: number): React.ReactElement => {
      return (
        <Animated.View
          key={item?.id || index}
          entering={
            userId === item.sender_id || item.role === 'user'
              ? ZoomInEasyDown
              : FadeIn
          }>
          {_renderChatItem({ item })}
        </Animated.View>
      );
    };

    const MemoizedRenderItem = useCallback(
      ({ item, index }: { item: MessageItem, index: number }) => _renderItem(item, index),
      [userId, isFavorite, isSpeaking, memoizedData, showDebug, onDebugButtonPress, references, metadata] // Dependencies that affect rendering
    );

    /**
     * render empty component.
     */
    const _renderEmpty = () => {
      if (isError) {
        return (
          <EmptyListContainer
            responsiveWidth={wdp(80)}
            responsiveHeight={hdp(100)}
            style={{
              minHeight: hdp(80),
              top: headerHeight,
              backgroundColor: 'transparent',
            }}
            entering={FadeInDown}
            exiting={FadeOut}>
            <EmptyList
              iconName={'alert'}
              iconColor={theme.primary}
              iconSize={theme.text.s1 * 2.5}
              onRefreshPress={onRefresh}
              title={'app.error'}
              titleStyle={{ color: theme.text.black }}
              showRefresh
            />
          </EmptyListContainer>
        );
      }
      if (isLoading) {
        return <ChatListItemSkeleton style={{ paddingTop: headerHeight }} />;
      }
      if ((data || []).length === 0 && !isLoading) {
        return props.isQNA ? (
          <EmptyChat
            userName={userName}
            onCheckAnswerPressed={props.onCheckAnswerPressed}
            question={props.question}
            setQuestion={props.setQuestion}
            onRecordHandler={props.onRecordHandler}
            isRecording={props.isRecording}
            introQuestions={props.introQuestions || []}
            isIntroQuestionsLoading={props.isIntroQuestionsLoading}
            isIntroQuestionsError={props.isIntroQuestionsError}
            onIntroQuestionsRefreshPress={props.onIntroQuestionsRefreshPress}
            isQNA={props.isQNA}
            onQuestionPress={props.onQuestionPress}
          />
        ) : (
          <ClinicalCaseContainer
            headerHeight={headerHeight}
            isWeb={true}
            paddingHorizontal={wdp(3)}>
            <ClinicalCaseScrollView
              contentContainerStyle={{ flexGrow: 1 }}
              showsVerticalScrollIndicator={false}
              scrollEnabled={true}
              bounces={false}
              alwaysBounceVertical={false}>
              <ClinicalCaseCategories
                chat_categories={props.introQuestions || []}
                onQuestionPress={handleClinicalCaseCategoryPress}
                question={selectedCase || ''}
                showTitle={true}
                isLoading={!props.introQuestions?.length}
                isNewChat={data.length === 0}
              />
            </ClinicalCaseScrollView>
          </ClinicalCaseContainer>
        );
      }
      return null;
    };

    const renderWebMessagesList = () => {
      if (memoizedData.length === 0) return null;


      return (
        <WebMessagesContainer
          ref={messagesContainerRef}
          headerHeight={headerHeight}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
          isEmpty={memoizedData.length === 0}>
          <WebMessagesList>
            {_renderHeader()}
            {memoizedData.map((item, index) => MemoizedRenderItem({ item, index }))}
            {_renderFooter()}
          </WebMessagesList>
        </WebMessagesContainer>
      );
    };


    /**
     * Handles clinical case category selection
     */
    const handleClinicalCaseCategoryPress = (item: ChatCategory) => {
      const { id: userId, specialty_id } = currentUser || {};

      // Get category_key for the selected category
      const categoryKey = getCategoryKeyById(item?.id);

      const body = {
        question: selectedCase || '',
        specialty_id: specialty_id || -1,
        question_id: item?.id,
        user_id: userId || -1,
        thread_id: undefined,
        tab_id: '2',
        // Include category_key for proper checkbox initialization
        category_key: categoryKey,
      };


      sendMessage(body);

      (navigation as any)?.navigate(screens.content.genioChat, {
        isQNA: !(item?.id),
        tab_id: '2',
        drugQuestion: selectedCase,
        categoryId: item?.id,
        // Pass category_key to the navigation state as well
        categoryKey,
      });
    };

    return (
      <Container entering={FadeInDown.delay(200)}>
        {(data || []).length > 0 && renderWebMessagesList()}
        {_renderEmpty()}
        {messageSettings()}
        {imageShow()}
        <FabContainer style={scrollButtonStyle}>
          <Animated.View style={fabButtonStyle}>
            <FabButton onPress={handleFabPress}>
              <Icon
                name={'keyboardArrowDown'}
                size={theme.text.s55}
                color={theme.text.dark}
              />
            </FabButton>
          </Animated.View>
        </FabContainer>

        {/* Treatment Options FAB for Differential Diagnosis */}
        {showTreatmentOptionsFAB && (
          <TreatmentOptionsFAB onPress={handleTreatmentOptionsFAB} />
        )}

        {/* Drugs Treatment FAB for Latest Treatment Options */}
        {showLatestTreatmentFAB && (
          <DrugsTreatmentFAB onPress={handleDrugsTreatmentFAB} />
        )}

        {/* Generate UCAF FAB for Drugs Treatment */}
        {shouldShowUCAFFABFinal && (
          <UCAFFAB onPress={handleUCAFFAB} />
        )}
      </Container>
    );
  },
);

export default React.memo(ChatComponentWeb);
